/**
 * Viewport Observer Module
 *
 * Uses IntersectionObserver to track when images enter/leave the viewport.
 * Also handles visibility changes for pausing animations in hidden tabs.
 */

import { TrackedImage, ViewportCallback } from './types';

/**
 * ViewportObserver class
 *
 * Efficiently tracks image visibility using IntersectionObserver.
 */
export class ViewportObserver {
    private observer: IntersectionObserver | null = null;
    private visibilityHandler: (() => void) | null = null;
    private callback: ViewportCallback;
    private threshold: number;
    private rootMargin: string;
    private observedElements: Map<HTMLElement, TrackedImage> = new Map();
    private isDocumentVisible: boolean = true;
    private debug: boolean;

    /**
     * Calculate priority based on distance from viewport center
     * Lower number = higher priority (closer to center)
     */
    static calculatePriority(element: HTMLElement): number {
        if (typeof window === 'undefined') return 0;

        const rect = element.getBoundingClientRect();
        const viewportCenter = window.innerHeight / 2;
        const elementCenter = rect.top + rect.height / 2;
        return Math.abs(elementCenter - viewportCenter);
    }

    constructor(
        callback: ViewportCallback,
        options: {
            threshold?: number;
            rootMargin?: string;
            debug?: boolean;
        } = {}
    ) {
        this.callback = callback;
        this.threshold = options.threshold ?? 0.1;
        this.rootMargin = options.rootMargin ?? '50px';
        this.debug = options.debug ?? false;
        this.isDocumentVisible = typeof document !== 'undefined' ? !document.hidden : true;
    }

    /**
     * Initialize the observer
     */
    init(): void {
        if (this.observer) {
            return;
        }

        // Create IntersectionObserver
        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const image = this.observedElements.get(entry.target as HTMLElement);
                    if (image && this.isDocumentVisible) {
                        if (this.debug) {
                            console.log(
                                '[beautifi] Viewport:',
                                entry.isIntersecting ? 'enter' : 'leave',
                                image.imageUrl
                            );
                        }
                        this.callback(image, entry.isIntersecting);
                    }
                });
            },
            {
                threshold: this.threshold,
                rootMargin: this.rootMargin,
            }
        );

        // Listen for visibility changes
        this.visibilityHandler = () => {
            const wasVisible = this.isDocumentVisible;
            this.isDocumentVisible = !document.hidden;

            if (wasVisible !== this.isDocumentVisible) {
                if (this.debug) {
                    console.log('[LiveMyPhotos] Document visibility:', this.isDocumentVisible);
                }

                // Notify all observed images of visibility change
                this.observedElements.forEach((image) => {
                    // When document becomes visible, re-check if images are in viewport
                    // When hidden, notify that images are "leaving" viewport
                    this.callback(image, this.isDocumentVisible);
                });
            }
        };

        document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    /**
     * Start observing an image
     */
    observe(image: TrackedImage): void {
        if (!this.observer) {
            this.init();
        }

        if (!this.observedElements.has(image.element)) {
            this.observedElements.set(image.element, image);
            this.observer!.observe(image.element);
        }
    }

    /**
     * Stop observing an image
     */
    unobserve(image: TrackedImage): void {
        if (this.observer && this.observedElements.has(image.element)) {
            this.observer.unobserve(image.element);
            this.observedElements.delete(image.element);
        }
    }

    /**
     * Check if document is currently visible
     */
    isVisible(): boolean {
        return this.isDocumentVisible;
    }

    /**
     * Clean up resources
     */
    destroy(): void {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }

        if (this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
            this.visibilityHandler = null;
        }

        this.observedElements.clear();
    }
}
